/**
 * This module provides an API to evaluate the configured expressions on the given data.
 * @module ExpressionEngine
 */
import Utils from '../utils/utils';
import { evaluateExpressionOnLocalData } from '../annotation/handlers/expression/utils';
import type { CreateC11nEnv } from '../interpreter/types';

import DeclareExpression from './declare-expression-handler';

const declareExpressions: {
  [context: string]: {
    [propertyReference: string]: string;
  };
} = {};

/**
 * Checks whether property ref exist in declare
 * expression.
 * @param propertyRef property ref path
 * @param context context name
 * @private
 */
export const hasDeclareExpression = (propertyRef: string, context: string) => {
  return declareExpressions[context] && Object.hasOwn(declareExpressions[context], propertyRef);
};

/**
 * Handles the changes event triggered by infra.
 * If property exist in declare expression map then it
 * will process it.
 * @param payload redux stateobject
 * @private
 */
export const handleChange = (payload: { context: string; reference: string; value: string }) => {
  const { context } = payload;
  if (hasDeclareExpression(payload.reference, context)) {
    DeclareExpression.handleExpression(
      payload.reference,
      payload.value,
      declareExpressions[context][payload.reference],
      context
    );
  }
};

/**
 * Updates the properties to existing map.
 * @param properties List of properties
 * @private
 */
export const updateDeclareExpressions = (properties = {}) => {
  Utils.copy(properties, declareExpressions);
};

/**
 * This API evaluates the filter expression on the given localData and returns a boolean true/false
 * The localData will always take precedence over the data in the store(i.e when we pass both data and c11nEnv, the data will be taken into consideration)
 * To evaluate expression on the data in the store, send data as undefined
 * @example <caption>Example for evaluateExpression</caption>
 *  const data =  { class: 'MOBILE_PAGE', HomeNumber: '456', name: 'test' };
 *  * const meta = {
 *    meta: {
 *      type: 'ScalarList',
 *      config: {
 *        ruleClass: 'B1-TestApp-Data-Address',
 *        value: '@FILTERED_LIST .Mobile[.HomeNumber != "123"].name'
 *      }
 *    },
 *    options: {
 *      pageReference: 'caseInfo.content',
 *      context: 'app'
 *    }
 *  };
 * const pConnect = PCore.createC11nEnv(meta);
 * const options = {
 *  pConnect
 * }
 * const result = PCore.getExpressionEngine().evaluate(".HomeNumber != '123'", data, options);
 *
 * options parameter is optional
 * @function
 * @param expression filterExpression for the given localData
 * @param data localData which has to be filtered (this is optional if c11nEnv object is passed in the options)
 * @param options  contains c11nEnv object of the property on which the given filterExpression is present
 * @returns result the evaluated value filterExpression for the given data
 */
export const evaluate = (expression: string, data: object, options: { pConnect: CreateC11nEnv }) => {
  return evaluateExpressionOnLocalData(expression, data, options?.pConnect.getPConnect());
};
